import { useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, InputAdornment, Typography } from '@mui/material';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import useAiChatPanel from '../../../hooks/useAiChatPanel';
import useAccount from '../../../hooks/useAccount';
import { AGENT_SESSIONS, CHAT_HISTORY } from '../../../constants/queryKeys';
import { Flex, FlexColumn } from '../../Flex';
import OverflowTooltip from '../../OverflowTooltip';
import SvgIcon from '../../SvgIcon/SvgIcon';
import TextField from '../../TextField';
import { ReactComponent as SearchIcon } from '../../../assets/search.svg';
import { ReactComponent as SlackIcon } from '../../../assets/16x16/slack-mono.svg';
import {
  fetchChatHistory,
  fetchChatMessages,
  fetchSessionHistory,
} from '../../../services/api';
import { localizeDate } from '../../../utils/datetime';
import {
  HISTORY_SINCE_MILLISECONDS,
  HISTORY_PAGE_SIZE,
  SESSION_ACTIVE_STATUSES,
} from '../constants';
import { HistoryData } from '../hooks/useChatHistory';
import type { SessionStatus } from '../../../types';
import { processHistoryMessages } from '../utils';

const ItemsList = styled(FlexColumn)`
  gap: 8px;
  padding: 16px 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const HistoryItem = styled(Flex)<{ $isActive?: boolean }>`
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.palette.common.gray10 : 'none'};
  padding: 12px 24px;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray2};
  transition: background 0.15s;
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray10};
  }
`;

const StatusDot = styled.span<{ $active?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $active, theme }) =>
    $active ? theme.palette.warning.main : 'transparent'};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.palette.warning.main : theme.palette.common.gray12};
`;

const TriggerSourceIcon = ({ source }: { source?: string }) => {
  if (source === 'slack') {
    return (
      <SvgIcon
        width={14}
        height={14}
        strokeColor="gray4"
        style={{ marginLeft: 'auto', flexShrink: 0 }}
      >
        <SlackIcon />
      </SvgIcon>
    );
  }
  return null;
};

const GroupLabel = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray4};
  padding: 8px 24px;
`;

const getTimeGroup = (time: string): string => {
  const date = localizeDate(time);
  const now = localizeDate(new Date());

  if (date.isSame(now, 'day')) return 'Today';
  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  if (date.isSame(now, 'week')) return 'This week';
  if (date.isSame(now, 'month')) return 'This month';
  return 'Older';
};

type HistoryEntry = {
  id: string;
  title: string;
  time: string;
  status?: SessionStatus;
  triggerSource?: string;
};

type ChatGroup = {
  label: string;
  chats: HistoryEntry[];
};

const groupChats = (chats: HistoryEntry[]): ChatGroup[] => {
  const groups: ChatGroup[] = [];

  for (const chat of chats) {
    const label = getTimeGroup(chat.time);
    const lastGroup = groups[groups.length - 1];

    if (lastGroup?.label === label) {
      lastGroup.chats.push(chat);
    } else {
      groups.push({ label, chats: [chat] });
    }
  }

  return groups;
};

type Props = {
  onSelectHistory: (data: HistoryData) => void;
  onThreadLoadStart?: () => void;
  activeThreadId?: string;
};

const ChatHistoryPanel = ({
  onSelectHistory,
  onThreadLoadStart,
  activeThreadId,
}: Props) => {
  const { applyThreadEnvScope } = useAiChatPanel();
  const { data: account } = useAccount();
  const useNewInfra = !!account?.new_agents_infra;
  const sentinelRef = useRef<HTMLDivElement>(null);

  const legacyQuery = useInfiniteQuery({
    queryKey: [CHAT_HISTORY],
    enabled: !useNewInfra,
    queryFn: ({ pageParam = 0 }) =>
      fetchChatHistory(
        new Date(Date.now() - HISTORY_SINCE_MILLISECONDS).toISOString(),
        HISTORY_PAGE_SIZE,
        pageParam,
      ),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (sum, page) => sum + page.chats.length,
        0,
      );
      return loadedCount < lastPage.total_count ? loadedCount : undefined;
    },
  });

  const sessionQuery = useInfiniteQuery({
    queryKey: [AGENT_SESSIONS],
    enabled: useNewInfra,
    queryFn: ({ pageParam = 0 }) =>
      fetchSessionHistory(HISTORY_PAGE_SIZE, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (sum, page) => sum + page.sessions.length,
        0,
      );
      return lastPage.sessions.length === HISTORY_PAGE_SIZE
        ? loadedCount
        : undefined;
    },
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = useNewInfra
    ? sessionQuery
    : legacyQuery;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const [search, setSearch] = useState('');
  const [optimisticThreadId, setOptimisticThreadId] = useState<string>();

  useEffect(() => {
    if (activeThreadId === optimisticThreadId) {
      setOptimisticThreadId(undefined);
    }
  }, [activeThreadId, optimisticThreadId]);

  const effectiveActiveId = optimisticThreadId ?? activeThreadId;

  const { mutate: fetchMessages } = useMutation({
    mutationFn: (id: string) => fetchChatMessages(id),
    onSuccess: (data, threadId) => {
      applyThreadEnvScope(data.environment_ids);
      onSelectHistory({
        threadId,
        messages: processHistoryMessages(data.messages),
      });
    },
  });

  const handleItemClick = (threadId: string) => {
    setOptimisticThreadId(threadId);
    onThreadLoadStart?.();
    if (useNewInfra) {
      onSelectHistory({ threadId, messages: [] });
      return;
    }
    fetchMessages(threadId);
  };

  const allChats = useMemo<HistoryEntry[]>(() => {
    if (useNewInfra) {
      return (
        sessionQuery.data?.pages.flatMap((page) =>
          page.sessions.map((session) => ({
            id: session.session_id,
            title: session.conversation_starter || '—',
            time: session.created_at,
            status: session.status,
            triggerSource: session.trigger_source,
          })),
        ) ?? []
      );
    }
    return (
      legacyQuery.data?.pages.flatMap((page) =>
        page.chats.map((chat) => ({
          id: chat.thread_id,
          title: chat.conversation_starter || '—',
          time: chat.time,
        })),
      ) ?? []
    );
  }, [useNewInfra, sessionQuery.data, legacyQuery.data]);

  const filteredChats = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) return allChats;
    return allChats.filter((chat) =>
      chat.title.toLowerCase().includes(trimmed),
    );
  }, [allChats, search]);

  const groups = useMemo(() => groupChats(filteredChats), [filteredChats]);

  return (
    <>
      <Flex sx={{ px: 3, pt: 2, flexShrink: 0 }}>
        <TextField
          fullWidth
          placeholder="Search"
          variant="outlined"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          size="medium"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Flex>
      <ItemsList>
        {groups.map((group) => (
          <FlexColumn key={group.label}>
            <GroupLabel>{group.label}</GroupLabel>
            {group.chats.map((chat) => (
              <HistoryItem
                key={chat.id}
                $isActive={chat.id === effectiveActiveId}
                onClick={() => handleItemClick(chat.id)}
                sx={{ gap: 1 }}
              >
                {useNewInfra && (
                  <StatusDot
                    $active={
                      !!chat.status &&
                      SESSION_ACTIVE_STATUSES.includes(chat.status)
                    }
                  />
                )}
                <OverflowTooltip
                  typographySx={{ fontSize: 'inherit', color: 'inherit' }}
                >
                  {chat.title}
                </OverflowTooltip>
                {useNewInfra && (
                  <TriggerSourceIcon source={chat.triggerSource} />
                )}
              </HistoryItem>
            ))}
          </FlexColumn>
        ))}
        {hasNextPage && (
          <Flex
            ref={sentinelRef}
            sx={{ justifyContent: 'center', py: 2, flexShrink: 0 }}
          >
            {isFetchingNextPage && <CircularProgress size={20} />}
          </Flex>
        )}
      </ItemsList>
    </>
  );
};

export default ChatHistoryPanel;
